import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, Instagram, Twitter, Youtube, Globe } from 'lucide-react';
import { followApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const AjustesPage = () => {
  const { user, setUser, isAuthenticated, logout, login } = useAuth();
  const navigate = useNavigate();
  const [nick, setNick] = useState(user?.nickname || '');
  const [nickSaved, setNickSaved] = useState(false);
  const [profileData, setProfileData] = useState({
    bio: user?.bio || '',
    instagram: user?.instagram || '',
    twitter: user?.twitter || '',
    youtube: user?.youtube || '',
    website: user?.website || ''
  });
  const [profileSaved, setProfileSaved] = useState(false);

  // Sincronizar estados cuando el usuario carga de forma asíncrona
  useEffect(() => {
    if (user) {
      setProfileData({
        bio: user.bio || '',
        instagram: user.instagram || '',
        twitter: user.twitter || '',
        youtube: user.youtube || '',
        website: user.website || ''
      });
      setNick(user.nickname || '');
    }
  }, [user]);

  const saveNickname = async () => {
    try {
      await followApi.updateProfile({ nickname: nick });
      setUser({ ...user, nickname: nick });
      setNickSaved(true);
      setTimeout(() => setNickSaved(false), 2000);
    } catch (error) {
      console.error('Error guardando nick:', error);
      alert(error.response?.data?.detail || 'Error guardando el nick');
    }
  };

  const saveProfile = async () => {
    try {
      await followApi.updateProfile(profileData);
      setUser({ ...user, ...profileData });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (error) {
      console.error('Error guardando perfil:', error);
      alert(error.response?.data?.detail || 'Error guardando el perfil');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-6xl mb-4">👤</p>
          <h2 className="text-xl font-bold text-white mb-2">Inicia sesión</h2>
          <p className="text-white/60 mb-4">Para ver tu perfil y audios</p>
          <button
            onClick={login}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full"
          >
            Entrar con Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/perfil')} className="text-white/60 p-2 -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-white font-bold">⚙️ Configuración</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Nick corto */}
        <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
          <label className="text-white/60 text-sm">Nick (se muestra en tus audios)</label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="text"
              maxLength={12}
              value={nick}
              onChange={(e) => setNick(e.target.value)}
              placeholder="Tu nick"
              className="flex-1 bg-white/10 text-white rounded-xl px-3 py-2 outline-none"
            />
            <button
              onClick={saveNickname}
              className="bg-purple-500 text-white px-4 py-2 rounded-xl text-sm"
            >
              Guardar
            </button>
            {nickSaved && <span className="text-green-400 text-sm">Guardado ✓</span>}
          </div>
        </div>

        {/* Perfil público */}
        <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10 space-y-4">
          <div>
            <label className="text-white/60 text-sm">Bio</label>
            <input
              type="text"
              value={profileData.bio}
              onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
              className="w-full bg-white/10 text-white rounded-xl px-3 py-2 mt-1 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/60 text-sm flex items-center gap-1"><Instagram size={14} /> Instagram</label>
              <input
                type="text"
                placeholder="@usuario"
                value={profileData.instagram}
                onChange={(e) => setProfileData({ ...profileData, instagram: e.target.value })}
                className="w-full bg-white/10 text-white rounded-xl px-3 py-2 mt-1 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm flex items-center gap-1"><Twitter size={14} /> Twitter</label>
              <input
                type="text"
                placeholder="@usuario"
                value={profileData.twitter}
                onChange={(e) => setProfileData({ ...profileData, twitter: e.target.value })}
                className="w-full bg-white/10 text-white rounded-xl px-3 py-2 mt-1 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm flex items-center gap-1"><Youtube size={14} /> YouTube</label>
              <input
                type="text"
                placeholder="@usuario"
                value={profileData.youtube}
                onChange={(e) => setProfileData({ ...profileData, youtube: e.target.value })}
                className="w-full bg-white/10 text-white rounded-xl px-3 py-2 mt-1 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm flex items-center gap-1"><Globe size={14} /> Web</label>
              <input
                type="text"
                placeholder="https://..."
                value={profileData.website}
                onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                className="w-full bg-white/10 text-white rounded-xl px-3 py-2 mt-1 outline-none text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={saveProfile}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-medium"
            >
              Guardar cambios
            </button>
            {profileSaved && <span className="text-green-400 text-sm">Guardado ✓</span>}
          </div>
        </div>

        <button
          onClick={logout}
          className="w-full bg-red-500/20 text-red-400 py-3 rounded-xl flex items-center justify-center gap-2"
        >
          <LogOut size={18} />
          Cerrar sesión
        </button>
      </main>
    </div>
  );
};

export default AjustesPage;
